import React, { useEffect } from 'react';
import { connect } from 'redux-bundler-react';

import ImageFigure from '@components/image-figure/ImageFigure';
import StepHeader from '@components/step-header/StepHeader';
import ExternalLink from '@components/external-link/ExternalLink';
import JurisdictionScientificMethods from '@src/app-pages/Resources/components/Jurisdiction/Jurisdiction.scientificMethods';
import LinkButton from '@components/link/linkButton';
import { FileTypes, ResourceFiles } from '@src/utils/enums';
import { useRequest } from '@stores';

export const JDFormInstructionsMetadata = {
  header: 'Request Jurisdictional Determination (Applicants/Landowners)',
  sectionName: 'Instructions',
};

const JDFormInstructions = connect('doDownloadFile', ({ doDownloadFile, stepNo }) => {
  const updateSection = useRequest((state) => state.updateSection);

  useEffect(() => {
    updateSection(stepNo, { valid: true, label: JDFormInstructionsMetadata.sectionName });
  }, [updateSection, stepNo]);

  return (
    <>
      <StepHeader text='Instructions' />
      <p>
        This request provides a way for agents or individuals to electronically submit{' '}
        <span className='text-bold'>delineation reports</span> and/or request a{' '}
        <span className='text-bold'>written jurisdictional determination.</span> This electronic application will guide
        you through providing the information necessary to the United States Army Corps of Engineers (USACE) to
        efficiently process your request.
      </p>

      <h4>Delineation of waters of the United States</h4>
      <p className='margin-top-3'>
        A <span className='text-bold'>delineation of waters of the United States</span> is the act of identifying and
        locating aquatic resources (including wetlands) within a specified area using scientific methods developed by
        the U.S. Army Corps of Engineers. A <span className='text-bold'>delineation report</span> is a term often used
        to describe a document or series of documents, stored in the USACE administrative record for the project, that
        describes the methodology used to conduct the delineation and typically includes a collection of one or more
        maps, datasheets, photographs, and/or figures that depict and support a legally defensible delineation of waters
        of the United States on a property. Agents typically submit delineation reports to the USACE in support of
        permit applications and/or jurisdictional determination requests. The submission of a delineation report by an
        agent helps accelerate the USACE review of the project but is{' '}
        <span className='text-bold'>strictly voluntary</span>. Agents should use the example maps/figures/templates and
        methods provided in these instructions to create the delineation report. Following these examples will help
        facilitate an efficient review of the delineation and prevent the need for the USACE to request and/or collect
        additional information. The USACE project manager ultimately determines the level of information necessary to
        support the delineation. This <ExternalLink href='https://youtu.be/ozlREOyAZdg ' content='video' /> provides
        information concerning the various types of aquatic resources that are subject to USACE permitting requirements
        and the authorities under which USACE administers its regulatory program.
      </p>

      <h4>Delineation Methods and Resources</h4>
      <JurisdictionScientificMethods />

      <h4>Jurisdictional Determination</h4>
      <p>
        Obtaining a Jurisdictional Determination (JD) typically involves a two-step process of 1) identifying and
        delineating the geographic extent of all aquatic resources (including wetlands) on a property through the
        creation of a delineation report and 2) the USACE issuing a written determination identifying which delineated
        areas are or may be (depending on the type of JD requested) regulated by the USACE under section 404 of the
        Clean Water Act and/or Section 10 of the Rivers and Harbors Act. Jurisdictional determinations are not required
        for the USACE to issue a permit but can provide a level of written assurance regarding the accuracy of the
        delineated waters during the planning phase of a project.
      </p>

      <div className='text-center'>
        <ImageFigure
          src='request-graphics/RRS_Graphic_V3_042623.png'
          alt='USACE jurisdiction in tidal waters'
          caption='This figure is a graphical depiction of the lateral limits of the USACE jurisdiction in tidal waters'
          style={{ width: '700px' }}
        />
      </div>
      <div className='text-center margin-bottom-3'>
        <ImageFigure
          src='request-graphics/RRS_Graphic_V2_041423.png'
          alt='USACE jurisdiction in fresh waters'
          caption='This figure is a graphical depiction of the lateral limits of the USACE jurisdiction in fresh waters (non-tidal waters).'
          style={{ width: '700px' }}
        />
      </div>

      <h4>The USACE provides two types of JDs</h4>
      <div className='margin-bottom-3' />

      <h5 className='margin-top-2'>Approved Jurisdictional Determination (AJD)</h5>
      <p>
        An AJD is defined in USACE regulations at 33 CFR 331.2. As explained in further detail in{' '}
        <ExternalLink
          href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll9/id/1256'
          title='RGL 16-01'
          content='RGL 16-01'
        />
        , an AJD is used to indicate that the USACE has identified the presence or absence of wetlands and/or other
        aquatic resources on a site, including their accurate location(s) and boundaries, as well as their
        jurisdictional status. AJDs are valid for 5 years.
      </p>

      <h5 className='margin-top-2'>Preliminary Jurisdictional Determination (PJD)</h5>
      <p>
        A PJD is defined in USACE regulations at 33 CFR 331.2. As explained in further detail in{' '}
        <ExternalLink
          href='https://usace.contentdm.oclc.org/utils/getfile/collection/p16021coll9/id/1256'
          title='RGL 16-01'
          content='RGL 16-01'
        />
        , a PJD is used to indicate that the USACE has identified the approximate location(s) and boundaries of wetlands
        and/or other aquatic resources on a site that are presumed to be subject to regulatory jurisdiction of the U.S.
        Army Corps of Engineers. Unlike an AJD, a PJD does not represent a definitive, official determination that there
        are, or that there are not, jurisdictional aquatic resources on a site, and does not have an expiration date.
      </p>
      <div className='margin-top-2'>
        <LinkButton
          onClick={() => doDownloadFile(ResourceFiles.Reg101FreqUsedTerms, FileTypes.Resource)}
          title='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
          content='RRS Commonly Used Regulatory Terms [PDF, 9 pages]'
        />
      </div>
    </>
  );
});

JDFormInstructions.metadata = JDFormInstructionsMetadata;

export default JDFormInstructions;
